import React from 'react';
import { FlyerCampaign } from '../../../../models/interfaces/FlyerCampaign.interface';
import logger from '../../../../commons/logger';
import flyerCampaignService from '../../../../services/flyer-campaign.service';
import { StringUtils } from '../../../../commons/utils/string.util';
import { Button, Grid, LinearProgress, TextField } from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { customTheme } from '../../../../styles/theme';
import FlyerCampaignTableComponent from './components/flyer-campaign-table.component';
import FlyerCampaignDialogComponent from './components/flyer-campaign-dialog.component';

const useStyles = makeStyles({
  filterForm: {
    marginBottom: customTheme.spacing(1),
  },
});

export default function FlyerCampaignPage() {
  const [flyerCampaigns, setFlyerCampaigns] = React.useState<FlyerCampaign[]>([]);
  const [filteredFlyerCampaigns, setFilteredFlyerCampaigns] = React.useState<FlyerCampaign[]>([]);

  const [searchKeyword, setSearchKeyword] = React.useState<string>(null);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [dataForDialog, setDataForDialog] = React.useState<FlyerCampaign>(null);
  const [isOpenFlyerCampaignDialog, setIsOpenFlyerCampaignDialog] = React.useState<boolean>(false);

  const _initData = async () => {
    setIsLoading(true);

    try {
      const data = await flyerCampaignService.getAllFlyerCampaigns();
      setFlyerCampaigns(data);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    _initData();
  }, []);

  React.useEffect(() => {
    const _updateFlyerCampaignsToDisplay = () => {
      const keyword = StringUtils.simplifyString(searchKeyword);

      setFilteredFlyerCampaigns(
        flyerCampaigns.filter(fc => {
          if (!keyword?.length) {
            return true;
          }

          if (StringUtils.simplifyString(fc.name).includes(keyword)) {
            return true;
          }

          if (StringUtils.simplifyString(fc.userEmail).includes(keyword)) {
            return true;
          }

          if (StringUtils.simplifyString(fc.address).includes(keyword)) {
            return true;
          }

          if (StringUtils.simplifyString(fc.smsMessage).includes(keyword)) {
            return true;
          }

          if (StringUtils.simplifyString(fc.emailMessage).includes(keyword)) {
            return true;
          }

          return false;
        })
      );
    };

    _updateFlyerCampaignsToDisplay();
  }, [searchKeyword, flyerCampaigns]);

  const onSelectFlyerCampaign = (flyerCampaign: FlyerCampaign) => {
    setDataForDialog(flyerCampaign);
    setIsOpenFlyerCampaignDialog(true);
  };

  const onCloseFlyerCampaignDialog = () => {
    setIsOpenFlyerCampaignDialog(false);
    _initData();
  };

  const classes = useStyles();

  return (
    <>
      {isLoading ? <LinearProgress /> : <></>}

      {isOpenFlyerCampaignDialog ? (
        <FlyerCampaignDialogComponent
          isOpen={isOpenFlyerCampaignDialog}
          data={dataForDialog}
          onClose={onCloseFlyerCampaignDialog}
        ></FlyerCampaignDialogComponent>
      ) : (
        <></>
      )}

      <Grid container spacing={3} className={classes.filterForm}>
        <Grid item xs={12} md={6}>
          <TextField
            variant="outlined"
            margin="dense"
            type="text"
            fullWidth
            label="Search"
            name="search"
            value={searchKeyword || ''}
            onChange={event => setSearchKeyword(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            startIcon={<PersonAdd />}
            onClick={() => onSelectFlyerCampaign(null)}
          >
            Create new flyer campaign
          </Button>
        </Grid>
      </Grid>

      <FlyerCampaignTableComponent
        flyerCampaigns={filteredFlyerCampaigns}
        onSelect={onSelectFlyerCampaign}
      ></FlyerCampaignTableComponent>
    </>
  );
}
